<template>
    <a
        class="router-link"
        v-bind="$attrs"
        :href="href"
        @click.capture="navigateTo"
    >
        <slot></slot>
    </a>
</template>
<script setup lang="ts">
import { useHelper } from '@/composeables';
import type { Route } from '@/objects/Route';
import type { IRouteParams } from '@/objects/RouteParams';
import { Router, type RouteName } from '@/objects/Router';
import { useEventStoreRef } from '@/stores';
import { computed } from 'vue';

defineOptions({ inheritAttrs: false });

const { eventID } = useEventStoreRef();

const props = defineProps<
    {
        /**
         * The name of the route to navigate to
         */
        route?: RouteName;

        /**
         * An actual path to navigate to
         */
        path?: string;

        /**
         * In case we provide a proper href
         * @remarks this is just of rendering and is only used for the href attribute. This disables the router click event but emits the click event
         */
        href?: string;
    } & IRouteParams
>();

/**
 * Define the emits
 */
const emit = defineEmits<{
    (e: 'click', url: string): void;
}>();

/**
 * We validate the path and return it
 * @remarks this is just of rendering and is only used for the href attribute
 */
const href = computed(() => {
    if (props.href) {
        return props.href;
    }

    let route: Route | null = null;

    if (props.path) {
        route = Router.GetRouteFromPath(props.path);
    } else if (props.route) {
        route = Router.GetRouteFromNameAndParams(props.route!, {
            code: props.code,
            eventID: props.eventID ?? eventID.value,
            id: props.id,
            subtab: props.subtab,
            tab: props.tab,
        });
    }

    if (!route) {
        // This mean we dont have any route to navigate to so we return an empty string. Most likely the link is handled with a click event
        return '';
    }

    return route.href;
});

/**
 * The navigation function
 * @param event
 */
const navigateTo = (event: Event) => {
    if (props.href) {
        emit('click', props.href);
    } else {
        let params: IRouteParams | undefined = {
            code: props.code,
            eventID: props.eventID ?? eventID.value,
            id: props.id,
            subtab: props.subtab,
            tab: props.tab,
        };
        let routename = props.route;

        if (props.path) {
            const route = Router.GetRouteFromPath(props.path);
            params = route?.params;
            routename = route?.name;
        }

        Router.NavigateTo(params ?? {}, routename);
    }
    return useHelper().stopPropagation(event);
};
</script>
